import { useEffect, useState, useCallback } from "react";
import DashboardShell from "@/components/DashboardShell";
import api, { formatApiError, downloadInvoicePdf, downloadPdf, fileUrl } from "@/lib/api";
import OrderMiniMap from "@/components/OrderMiniMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, Package, Users, Tag, Truck, FileText, CreditCard, Settings as SettingsIcon,
  Plus, Trash2, Check, AlertTriangle, Download, Pencil, Repeat, Pause, Play, Mail, MessageSquare, QrCode, Scale, History, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const money = (n) => "£" + Number(n || 0).toFixed(2);
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "orders", label: "Orders", icon: Package },
  { key: "recurring", label: "Recurring", icon: Repeat },
  { key: "clients", label: "Clients", icon: Users },
  { key: "pricing", label: "Price list", icon: Tag },
  { key: "drivers", label: "Drivers & jobs", icon: Truck },
  { key: "invoices", label: "Invoices", icon: FileText },
  { key: "subscription", label: "Subscription", icon: CreditCard },
  { key: "account", label: "Settings", icon: SettingsIcon },
];
const STATUS_COLORS = {
  new: "bg-slate-100 text-slate-700", collected: "bg-blue-100 text-blue-700",
  processing: "bg-amber-100 text-amber-700", ready: "bg-teal-100 text-teal-700",
  out_for_delivery: "bg-indigo-100 text-indigo-700", completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};


function Orders() {
  const [orders, setOrders] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [weighing, setWeighing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState({ client_id: "", pickup_date: "", pickup_slot: "", notes: "", lines: [] });

  const load = useCallback(() => {
    api.get("/business/orders").then((r) => { setOrders(r.data); setLoaded(true); });
    api.get("/business/clients").then((r) => setClients(r.data));
    api.get("/business/items").then((r) => setItems(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addLine = (item) => setForm((f) => {
    const ex = f.lines.find((l) => l.item_id === item.id);
    if (ex) return { ...f, lines: f.lines.map((l) => l.item_id === item.id ? { ...l, qty: l.qty + 1 } : l) };
    return { ...f, lines: [...f.lines, { item_id: item.id, name: item.name, price: item.price, qty: 1 }] };
  });
  const setQty = (id, qty) => setForm((f) => ({ ...f, lines: f.lines.map((l) => l.item_id === id ? { ...l, qty: Math.max(1, qty) } : l) }));

  const create = async () => {
    if (!form.client_id) return toast.error("Choose a client");
    if (!form.lines.length) return toast.error("Add at least one item");
    try {
      await api.post("/business/orders", { client_id: form.client_id, items: form.lines,
        pickup_date: form.pickup_date, pickup_slot: form.pickup_slot, notes: form.notes });
      toast.success("Order created"); setOpen(false);
      setForm({ client_id: "", pickup_date: "", pickup_slot: "", notes: "", lines: [] }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/business/orders/${id}/status`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (o) => {
    if (!window.confirm(`Delete order #${o.ref}? This can't be undone.`)) return;
    try { await api.delete(`/business/orders/${o.id}`); toast.success("Order deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openEdit = (o) => setEditing({ id: o.id, ref: o.ref,
    lines: o.items.map((i) => ({ item_id: i.item_id, name: i.name, price: i.price, qty: i.qty })),
    pickup_date: o.pickup_date || "", pickup_slot: o.pickup_slot || "", notes: o.notes || "" });
  const eSetQty = (id, qty) => setEditing((ed) => ({ ...ed, lines: ed.lines.map((l) => l.item_id === id ? { ...l, qty: Math.max(1, qty) } : l) }));
  const eAdd = (item) => setEditing((ed) => {
    const ex = ed.lines.find((l) => l.item_id === item.id);
    if (ex) return { ...ed, lines: ed.lines.map((l) => l.item_id === item.id ? { ...l, qty: l.qty + 1 } : l) };
    return { ...ed, lines: [...ed.lines, { item_id: item.id, name: item.name, price: item.price, qty: 1 }] };
  });
  const eRemove = (id) => setEditing((ed) => ({ ...ed, lines: ed.lines.filter((l) => l.item_id !== id) }));
  const saveEdit = async () => {
    if (!editing.lines.length) return toast.error("An order needs at least one item");
    try { await api.put(`/business/orders/${editing.id}`, { items: editing.lines,
        pickup_date: editing.pickup_date, pickup_slot: editing.pickup_slot, notes: editing.notes });
      toast.success("Order updated"); setEditing(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openWeigh = (o) => setWeighing({ id: o.id, ref: o.ref,
    lines: o.items.map((i) => ({ name: i.name, unit: i.unit || "item", price: i.price, qty: i.qty })) });
  const setWeight = (idx, v) => setWeighing((wg) => ({ ...wg, lines: wg.lines.map((l, i) => i === idx ? { ...l, qty: v } : l) }));
  const saveWeigh = async () => {
    try {
      await api.put(`/business/orders/${weighing.id}/weigh`, { lines: weighing.lines.map((l, i) => ({ index: i, weight: Number(l.qty) || 0 })) });
      toast.success("Weights saved — total updated"); setWeighing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const orderTotal = form.lines.reduce((s, l) => s + l.price * l.qty, 0);

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Orders</h1>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => downloadPdf("/business/labels", "labels.pdf").catch((e) => toast.error(e.response?.status === 404 ? "No active orders to label" : "Label print failed"))} data-testid="bulk-labels-btn"><QrCode className="mr-1 h-4 w-4" /> Print all labels</Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full bg-brand hover:bg-brand-hover" data-testid="new-order-btn"><Plus className="mr-1 h-4 w-4" /> New order</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>New order</DialogTitle><DialogDescription>Create an order for one of your clients.</DialogDescription></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))}>
                  <SelectTrigger data-testid="order-client-select"><SelectValue placeholder="Select a client" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Pickup date</Label><Input type="date" value={form.pickup_date} onChange={(e) => setForm((f) => ({ ...f, pickup_date: e.target.value }))} data-testid="order-pickup-date" /></div>
                <div className="space-y-1.5"><Label>Pickup slot</Label><Input placeholder="09:00-11:00" value={form.pickup_slot} onChange={(e) => setForm((f) => ({ ...f, pickup_slot: e.target.value }))} data-testid="order-pickup-slot" /></div>
              </div>
              <div>
                <Label>Items</Label>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {items.map((it) => <button key={it.id} onClick={() => addLine(it)} className="rounded-full border border-slate-200 px-3 py-1 text-xs hover:border-brand hover:text-brand" data-testid={`order-add-item-${it.id}`}>+ {it.name} ({money(it.price)})</button>)}
                </div>
                <div className="mt-3 space-y-2">
                  {form.lines.map((l) => (
                    <div key={l.item_id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                      <span>{l.name}</span>
                      <div className="flex items-center gap-2">
                        <Input type="number" min="1" value={l.qty} onChange={(e) => setQty(l.item_id, Number(e.target.value))} className="h-8 w-16" />
                        <span className="w-16 text-right font-medium">{money(l.price * l.qty)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {form.lines.length > 0 && <p className="mt-2 text-right text-sm font-semibold">Total: {money(orderTotal)}</p>}
              </div>
              <Textarea placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="order-notes" />
            </div>
            <DialogFooter><Button onClick={create} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="order-create-btn">Create order</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
            <tr><th className="px-4 py-3">Ref</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Pickup</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Update</th><th className="px-4 py-3">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {orders.map((o) => (
              <tr key={o.id} data-testid={`order-row-${o.ref}`}>
                <td className="px-4 py-3 font-mono text-xs">{o.ref}</td>
                <td className="px-4 py-3">{o.client_name}</td>
                <td className="px-4 py-3 font-medium">{money(o.total)}</td>
                <td className="px-4 py-3 text-slate-500">{o.pickup_date || "—"} {o.pickup_slot}</td>
                <td className="px-4 py-3"><Badge className={`${STATUS_COLORS[o.status]} border-0`}>{o.status.replace(/_/g, " ")}</Badge></td>
                <td className="px-4 py-3">
                  <Select value={o.status} onValueChange={(v) => setStatus(o.id, v)}>
                    <SelectTrigger className="h-8 w-40" data-testid={`order-status-${o.ref}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{["new","collected","processing","ready","out_for_delivery","completed","cancelled"].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g," ")}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => downloadPdf(`/business/orders/${o.id}/label`, `label-${o.ref}.pdf`).catch(() => toast.error("Label failed"))} className="text-slate-400 hover:text-brand" title="Print QR label" data-testid={`order-label-${o.ref}`}><QrCode className="h-4 w-4" /></button>
                    <button onClick={() => openWeigh(o)} className={`hover:text-brand ${o.weight_captured ? "text-teal-600" : "text-slate-400"}`} title="Capture weights" data-testid={`order-weigh-${o.ref}`} disabled={!!o.invoice_id}><Scale className={`h-4 w-4 ${o.invoice_id ? "opacity-30" : ""}`} /></button>
                    <button onClick={() => setViewing(o)} className={`hover:text-brand ${o.delivery_photo ? "text-teal-600" : "text-slate-400"}`} title="Timeline & delivery proof" data-testid={`order-timeline-${o.ref}`}>{o.delivery_photo ? <ImageIcon className="h-4 w-4" /> : <History className="h-4 w-4" />}</button>
                    <button onClick={() => openEdit(o)} className="text-slate-400 hover:text-brand" title="Edit" data-testid={`order-edit-${o.ref}`} disabled={!!o.invoice_id}><Pencil className={`h-4 w-4 ${o.invoice_id ? "opacity-30" : ""}`} /></button>
                    <button onClick={() => del(o)} className="text-slate-300 hover:text-red-500" title="Delete" data-testid={`order-delete-${o.ref}`} disabled={!!o.invoice_id}><Trash2 className={`h-4 w-4 ${o.invoice_id ? "opacity-30" : ""}`} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!orders.length && <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-400">{loaded ? "No orders yet." : "Loading…"}</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit order {editing?.ref}</DialogTitle><DialogDescription>Adjust items, pickup and notes. Invoiced orders can&rsquo;t be edited.</DialogDescription></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>Add items</Label>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {items.map((it) => <button key={it.id} onClick={() => eAdd(it)} className="rounded-full border border-slate-200 px-3 py-1 text-xs hover:border-brand hover:text-brand" data-testid={`order-edit-add-${it.id}`}>+ {it.name}</button>)}
                </div>
                <div className="mt-3 space-y-2">
                  {editing.lines.map((l) => (
                    <div key={l.item_id} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                      <span>{l.name}</span>
                      <div className="flex items-center gap-2">
                        <Input type="number" min="1" value={l.qty} onChange={(e) => eSetQty(l.item_id, Number(e.target.value))} className="h-8 w-16" data-testid={`order-edit-qty-${l.item_id}`} />
                        <span className="w-16 text-right font-medium">{money(l.price * l.qty)}</span>
                        <button onClick={() => eRemove(l.item_id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-right text-sm font-semibold">Total: {money(editing.lines.reduce((s, l) => s + l.price * l.qty, 0))}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Pickup date</Label><Input type="date" value={editing.pickup_date} onChange={(e) => setEditing((ed) => ({ ...ed, pickup_date: e.target.value }))} data-testid="order-edit-pickup-date" /></div>
                <div className="space-y-1.5"><Label>Pickup slot</Label><Input value={editing.pickup_slot} onChange={(e) => setEditing((ed) => ({ ...ed, pickup_slot: e.target.value }))} data-testid="order-edit-pickup-slot" /></div>
              </div>
              <Textarea placeholder="Notes" value={editing.notes} onChange={(e) => setEditing((ed) => ({ ...ed, notes: e.target.value }))} data-testid="order-edit-notes" />
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="order-edit-save">Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!weighing} onOpenChange={(o) => !o && setWeighing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Capture weights · {weighing?.ref}</DialogTitle><DialogDescription>Enter the actual weight for kg-priced items. The order total updates automatically.</DialogDescription></DialogHeader>
          {weighing && (
            <div className="space-y-2">
              {weighing.lines.map((l, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm" data-testid={`weigh-line-${i}`}>
                  <div><p className="font-medium">{l.name}</p><p className="text-xs text-slate-400">{money(l.price)} / {l.unit}</p></div>
                  <div className="flex items-center gap-2">
                    <Input type="number" step="0.1" min="0" value={l.qty} onChange={(e) => setWeight(i, e.target.value)} className="h-9 w-24" data-testid={`weigh-input-${i}`} />
                    <span className="w-8 text-xs text-slate-500">{l.unit}</span>
                    <span className="w-16 text-right font-medium">{money(l.price * (Number(l.qty) || 0))}</span>
                  </div>
                </div>
              ))}
              <p className="mt-2 text-right font-semibold">New total: {money(weighing.lines.reduce((s, l) => s + l.price * (Number(l.qty) || 0), 0))}</p>
            </div>
          )}
          <DialogFooter><Button onClick={saveWeigh} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="weigh-save">Save weights</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Order {viewing?.ref} · timeline</DialogTitle><DialogDescription>Full status history and delivery proof for dispute resolution.</DialogDescription></DialogHeader>
          {viewing && (
            <div className="space-y-4">
              <div data-testid="order-map-section">
                <Label>Route</Label>
                <div className="mt-1.5"><OrderMiniMap orderId={viewing.id} /></div>
              </div>
              {viewing.delivery_photo && (
                <div data-testid="order-proof-photo">
                  <Label>Delivery proof</Label>
                  <a href={fileUrl(viewing.delivery_photo)} target="_blank" rel="noreferrer">
                    <img src={fileUrl(viewing.delivery_photo)} alt="Delivery proof" className="mt-1.5 max-h-64 w-full rounded-xl border border-slate-200 object-cover" onError={(e) => { e.currentTarget.style.display = "none"; if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = "block"; }} />
                    <p className="mt-1.5 hidden rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">Photo unavailable</p>
                  </a>
                </div>
              )}
              {viewing.signature && (
                <div data-testid="order-signature">
                  <Label>Customer signature</Label>
                  <a href={fileUrl(viewing.signature)} target="_blank" rel="noreferrer">
                    <img src={fileUrl(viewing.signature)} alt="Signature" className="mt-1.5 max-h-40 w-full rounded-xl border border-slate-200 bg-white object-contain p-2" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  </a>
                </div>
              )}
              <div>
                <Label>Status history</Label>
                <ol className="mt-2 space-y-2" data-testid="order-timeline-list">
                  {(viewing.status_history || []).slice().reverse().map((h, i) => (
                    <li key={i} className="flex items-start gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                      <Badge className={`${STATUS_COLORS[h.status] || "bg-slate-100 text-slate-700"} border-0 shrink-0`}>{(h.status || "").replace(/_/g, " ")}</Badge>
                      <div className="min-w-0">
                        <p className="text-slate-600">{h.note}</p>
                        <p className="text-xs text-slate-400">{(h.at || "").replace("T", " ").slice(0, 16)}</p>
                        {h.photo && <a href={fileUrl(h.photo)} target="_blank" rel="noreferrer" className="text-xs text-brand underline">View photo</a>}
                      </div>
                    </li>
                  ))}
                  {!(viewing.status_history || []).length && <li className="text-sm text-slate-400">No history yet.</li>}
                </ol>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}

/* ------------------------------ Recurring ------------------------------ */

export default Orders;
